import { ComponentType } from '@angular/cdk/portal';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, HostListener, inject, OnInit, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormRecord } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { AuthService } from 'src/app/services/auth-service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ComponentCanDeactivate } from 'src/app/services/unsaved-changes-guard';
import { UserGroupsApiService } from 'src/app/data-transfer/services/user-groups-api-service';
import { LinkedOrganizationApiService } from 'src/app/data-transfer/services/linked-organization-api-service';
import { LinkedOrganizationDto } from 'src/app/data-transfer/entities/linked-organization-dto';
import { OrganizationUserGroupDto, UserGroupMemberDto } from 'src/app/data-transfer/entities/user-group-entities/organization-user-group-dto';
import {
  OrganizationDirectoryDto,
  UserGroupDirectoryPermission,
  UserGroupDirectoryPermissionDto,
} from 'src/app/data-transfer/entities/user-group-entities/user-group-directory-permission-dto';
import { EMPTY_GUID } from 'src/app/data-transfer/entities/doc-entities/dataset-profile-dto';
import { UserGroupVisibleOrganizationDto } from 'src/app/data-transfer/entities/user-group-entities/user-group-visible-organization-dto';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import { SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { SimpleConfirmDialogComponent } from 'src/app/components/dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { SegmentedToggleOption } from 'src/app/components/shared-components/segmented-toggle/segmented-toggle.component';
import { getApiErrorMessage } from '../admin-api-error';
import { AdminDialogResult } from '../admin-dialog-result';
import { getUserDisplayName } from '../organization-user.util';
import { AdminGroupDialogComponent, AdminGroupDialogData, AdminGroupDialogResult } from '../admin-group-dialog/admin-group-dialog.component';
import { AdminGroupMemberDialogComponent, AdminGroupMemberDialogData, AdminGroupMemberDialogResult } from '../admin-group-member-dialog/admin-group-member-dialog.component';

const { None, Read, Write, NoAccess } = UserGroupDirectoryPermission;

const LEVEL_OPTIONS: [UserGroupDirectoryPermission, string][] = [
  [NoAccess, 'organizationAdmin.groups.folderPermissions.levelNoAccess'],
  [Read, 'organizationAdmin.groups.folderPermissions.levelRead'],
  [Write, 'organizationAdmin.groups.folderPermissions.levelWrite'],
];

interface FolderNode {
  directory: OrganizationDirectoryDto;
  depth: number;
  parentId: number | null;
}

interface FolderRow {
  id: number;
  name: string;
  isTopFolder: boolean;
  paddingLeft: string;
  level: UserGroupDirectoryPermission;
  hasNoAccess: boolean;
  hasSharedSubfolder: boolean;
  isUncovered: boolean;
  canCollapse: boolean;
  isExpanded: boolean;
}

type GroupSwitch = Partial<Pick<OrganizationUserGroupDto, 'isDirectoryPermissionsEnabled' | 'isVisibleOrganizationsEnabled'>>;

type ConfirmedData<R extends AdminDialogResult<unknown>> = Extract<R, { event: DialogActions.CONFIRM }>['data'];

const isNoAccess = (level: UserGroupDirectoryPermission | undefined): boolean => level === NoAccess;

const levelAt = (levels: Map<number, UserGroupDirectoryPermission>, id: number | null): UserGroupDirectoryPermission => (id != null ? (levels.get(id) ?? NoAccess) : NoAccess);

const levelOf = (own: UserGroupDirectoryPermission, parentLevel: UserGroupDirectoryPermission): UserGroupDirectoryPermission => (own === None ? parentLevel : own);

const storedValueFor = (level: UserGroupDirectoryPermission, parentLevel: UserGroupDirectoryPermission): UserGroupDirectoryPermission => (level === parentLevel ? None : level);

const computeLevels = (nodes: FolderNode[], values: Record<string, UserGroupDirectoryPermission>): Map<number, UserGroupDirectoryPermission> => {
  const levels = new Map<number, UserGroupDirectoryPermission>();
  nodes.forEach(({ directory, parentId }) => levels.set(directory.id, levelOf(values[directory.id] ?? None, levelAt(levels, parentId))));
  return levels;
};

const changedEntries = <T>(draft: Record<string, T>, saved: Record<string, T>): [number, T][] =>
  Object.entries(draft)
    .filter(([id, value]) => saved[id] !== value)
    .map(([id, value]) => [Number(id), value]);

const idsByValue = <T>(entries: [number, T][]): [T, number[]][] => {
  const groups = new Map<T, number[]>();
  entries.forEach(([id, value]) => {
    const ids = groups.get(value) ?? [];
    ids.push(id);
    groups.set(value, ids);
  });
  return [...groups];
};

const syncRecordControls = <T>(record: FormRecord<FormControl<T>>, values: Record<string, T>, defaultValue: T): void => {
  Object.keys(record.controls)
    .filter(id => !(id in values))
    .forEach(id => record.removeControl(id, { emitEvent: false }));
  Object.keys(values)
    .filter(id => !record.contains(id))
    .forEach(id => record.addControl(id, new FormControl(defaultValue, { nonNullable: true }) as FormControl<T>, { emitEvent: false }));
};

@Component({
  selector: 'app-admin-user-groups',
  templateUrl: './admin-user-groups.component.html',
  styleUrls: ['./admin-user-groups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminUserGroupsComponent implements OnInit, ComponentCanDeactivate {
  private readonly dialog = inject(MatDialog);
  private readonly userGroupsApiService = inject(UserGroupsApiService);
  private readonly linkedOrganizationApiService = inject(LinkedOrganizationApiService);
  private readonly translocoService = inject(TranslocoService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly allGroups = signal<OrganizationUserGroupDto[]>([]);
  protected readonly users = signal<UserGroupMemberDto[]>([]);
  private readonly directories = signal<OrganizationDirectoryDto[]>([]);
  private readonly directoryPermissions = signal<UserGroupDirectoryPermissionDto[]>([]);
  private readonly uncoveredDirectoryIds = signal<ReadonlySet<number>>(new Set());
  // The visible organizations aren't part of the release yet; set to true to show them again.
  protected readonly isOrganizationVisibilityEnabled = false;
  private readonly linkedOrganizations = signal<LinkedOrganizationDto[]>([]);
  private readonly visibleOrganizations = signal<UserGroupVisibleOrganizationDto[]>([]);
  private readonly uncoveredOrganizationIds = signal<ReadonlySet<number>>(new Set());
  private readonly collapsedIds = signal<ReadonlySet<number>>(new Set());
  private readonly autoSharedIds = signal<ReadonlySet<number>>(new Set());
  protected readonly selectedGroupId = signal<string>(EMPTY_GUID);
  protected readonly isLoading = signal(true);
  protected readonly isSaving = signal(false);

  protected readonly permissionsForm = new FormGroup({
    isRestricted: new FormControl(false, { nonNullable: true }),
    folders: new FormRecord<FormControl<UserGroupDirectoryPermission>>({}),
  });
  protected readonly permissionsDraft = toSignal(this.permissionsForm.valueChanges.pipe(map(() => this.permissionsForm.getRawValue())), {
    initialValue: this.permissionsForm.getRawValue(),
  });

  protected readonly visibilityForm = new FormGroup({
    isRestricted: new FormControl(false, { nonNullable: true }),
    organizations: new FormRecord<FormControl<boolean>>({}),
  });
  protected readonly visibilityDraft = toSignal(this.visibilityForm.valueChanges.pipe(map(() => this.visibilityForm.getRawValue())), {
    initialValue: this.visibilityForm.getRawValue(),
  });

  protected readonly folderSearchControl = new FormControl('', { nonNullable: true });
  private readonly folderSearch = toSignal(this.folderSearchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()), { initialValue: '' });

  private readonly claimsReloaded = toSignal(this.authService.claimsReloaded$, { equal: () => false });
  protected readonly canEdit = computed(() => {
    this.claimsReloaded();
    return this.authService.canManageOrganization();
  });

  private readonly activeLang = toSignal(this.translocoService.langChanges$);
  protected readonly permissionsToggleOptions = this.translatedOptions<boolean>([
    [false, 'organizationAdmin.groups.folderPermissions.toggleOff'],
    [true, 'organizationAdmin.groups.folderPermissions.toggleOn'],
  ]);
  protected readonly visibleOrganizationsToggleOptions = this.translatedOptions<boolean>([
    [false, 'organizationAdmin.groups.visibleOrganizations.allOrganizations'],
    [true, 'organizationAdmin.groups.permissionsRestricted'],
  ]);
  protected readonly levelToggleOptions = this.translatedOptions(LEVEL_OPTIONS);
  protected readonly parentLevelToggleOptions = computed(() =>
    this.levelToggleOptions().map(option =>
      option.value === NoAccess ? { ...option, disabled: true, disabledTooltip: this.translocoService.translate('organizationAdmin.groups.folderPermissions.denyBlocked') } : option
    )
  );

  protected readonly groups = computed(() => this.allGroups().filter(group => !group.isRootGroup));
  protected readonly selectedGroup = computed(() => this.groups().find(group => group.id === this.selectedGroupId()));

  protected readonly selectedMembers = computed(() => {
    const group = this.selectedGroup();
    if (!group) return [];
    return this.users()
      .filter(user => user.userGroupIds.includes(group.id))
      .map(user => ({ id: user.id, name: getUserDisplayName(user) || user.email }));
  });

  private readonly folderNodes = computed<FolderNode[]>(() => {
    const directories = this.directories()
      .filter(directory => !directory.isDeleted)
      .sort((a, b) => a.name.localeCompare(b.name));
    const ids = new Set(directories.map(directory => directory.id));
    const childrenByParent = new Map<number | null, OrganizationDirectoryDto[]>();
    directories.forEach(directory => {
      const parentId = directory.parentId !== null && ids.has(directory.parentId) ? directory.parentId : null;
      const children = childrenByParent.get(parentId) ?? [];
      children.push(directory);
      childrenByParent.set(parentId, children);
    });

    const nodes: FolderNode[] = [];
    const visit = (parentId: number | null, depth: number): void => {
      childrenByParent.get(parentId)?.forEach(directory => {
        nodes.push({ directory, depth, parentId });
        visit(directory.id, depth + 1);
      });
    };
    visit(null, 0);
    return nodes;
  });

  private readonly parentById = computed(() => new Map(this.folderNodes().map(({ directory, parentId }) => [directory.id, parentId])));
  private readonly childIdsById = computed(() => {
    const childIds = new Map<number, number[]>();
    this.folderNodes().forEach(({ directory, parentId }) => {
      if (parentId != null) (childIds.get(parentId) ?? childIds.set(parentId, []).get(parentId)!).push(directory.id);
    });
    return childIds;
  });

  private readonly savedPermissions = computed(() => {
    const group = this.selectedGroup();
    const stored = new Map(
      this.directoryPermissions()
        .filter(permission => permission.userGroupId === group?.id)
        .map(permission => [permission.directoryId, permission.permission])
    );
    return {
      isRestricted: group?.isDirectoryPermissionsEnabled ?? false,
      values: Object.fromEntries(this.folderNodes().map(({ directory }) => [directory.id, stored.get(directory.id) ?? None])) as Record<string, UserGroupDirectoryPermission>,
    };
  });

  private readonly levels = computed(() => computeLevels(this.folderNodes(), this.permissionsDraft().folders));
  private readonly savedLevels = computed(() => computeLevels(this.folderNodes(), this.savedPermissions().values));

  private readonly changedFolders = computed(() => changedEntries(this.permissionsDraft().folders, this.savedPermissions().values));

  protected readonly hasPermissionChanges = computed(() => {
    const savedLevels = this.savedLevels();
    const hasLevelChanges = [...this.levels()].some(([id, level]) => savedLevels.get(id) !== level);
    return hasLevelChanges || this.permissionsDraft().isRestricted !== this.savedPermissions().isRestricted;
  });
  protected readonly allFoldersLevel = computed(() => {
    const levels = new Set(this.levels().values());
    return levels.size === 1 ? [...levels][0] : null;
  });
  protected readonly hasNoFolderAccess = computed(() => this.permissionsDraft().isRestricted && [...this.levels().values()].every(isNoAccess));

  private readonly sharedParentIds = computed(() => {
    const levels = this.levels();
    const nodes = this.folderNodes();
    const ids = new Set<number>();
    for (let i = nodes.length - 1; i >= 0; i--) {
      const { directory, parentId } = nodes[i];
      if (parentId != null && (!isNoAccess(levels.get(directory.id)) || ids.has(directory.id))) ids.add(parentId);
    }
    return ids;
  });

  protected readonly folderRows = computed<FolderRow[]>(() => {
    const levels = this.levels();
    const sharedParentIds = this.sharedParentIds();
    const uncoveredDirectoryIds = this.uncoveredDirectoryIds();
    const parentIds = new Set(this.parentById().values());
    const collapsedIds = this.collapsedIds();
    return this.folderNodes().map(({ directory, depth }) => {
      const level = levels.get(directory.id) ?? NoAccess;
      return {
        id: directory.id,
        name: directory.name,
        isTopFolder: depth === 0,
        paddingLeft: `${12 + depth * 22}px`,
        level,
        hasNoAccess: isNoAccess(level),
        hasSharedSubfolder: sharedParentIds.has(directory.id),
        isUncovered: uncoveredDirectoryIds.has(directory.id),
        canCollapse: parentIds.has(directory.id),
        isExpanded: !collapsedIds.has(directory.id),
      };
    });
  });

  protected readonly filteredFolderRows = computed<FolderRow[]>(() => {
    const term = this.folderSearch().trim().toLowerCase();
    const rows = this.folderRows();
    const parentById = this.parentById();

    if (!term) {
      const collapsedIds = this.collapsedIds();
      const hiddenIds = new Set<number>();
      rows.forEach(row => {
        const parentId = parentById.get(row.id);
        if (parentId != null && (collapsedIds.has(parentId) || hiddenIds.has(parentId))) hiddenIds.add(row.id);
      });
      return rows.filter(row => !hiddenIds.has(row.id));
    }

    const visibleIds = new Set(rows.filter(row => row.name.toLowerCase().includes(term)).map(row => row.id));
    rows.forEach(row => {
      const parentId = parentById.get(row.id);
      if (parentId != null && visibleIds.has(parentId)) visibleIds.add(row.id);
    });
    for (let i = rows.length - 1; i >= 0; i--) {
      const parentId = parentById.get(rows[i].id);
      if (parentId != null && visibleIds.has(rows[i].id)) visibleIds.add(parentId);
    }
    return rows.filter(row => visibleIds.has(row.id)).map(row => ({ ...row, canCollapse: false }));
  });

  private readonly savedVisibility = computed(() => {
    const group = this.selectedGroup();
    const visibleIds = new Set(
      this.visibleOrganizations()
        .filter(visible => visible.userGroupId === group?.id)
        .map(visible => visible.organizationId)
    );
    return {
      isRestricted: group?.isVisibleOrganizationsEnabled ?? false,
      values: Object.fromEntries(this.linkedOrganizations().map(organization => [organization.id, visibleIds.has(organization.id)])) as Record<string, boolean>,
    };
  });

  private readonly changedOrganizations = computed(() => changedEntries(this.visibilityDraft().organizations, this.savedVisibility().values));

  protected readonly hasVisibilityChanges = computed(() => this.changedOrganizations().length > 0 || this.visibilityDraft().isRestricted !== this.savedVisibility().isRestricted);

  protected readonly visibleOrganizationRows = computed(() => {
    const draftOrganizations = this.visibilityDraft().organizations;
    const uncoveredOrganizationIds = this.uncoveredOrganizationIds();
    return this.linkedOrganizations().map(organization => ({
      organization,
      isVisible: draftOrganizations[organization.id] ?? false,
      isUncovered: uncoveredOrganizationIds.has(organization.id),
    }));
  });

  private readonly hasUnsavedChanges = computed(() => this.hasPermissionChanges() || this.hasVisibilityChanges());

  ngOnInit(): void {
    this.reload();
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.hasUnsavedChanges();
  }

  protected selectGroup(group: OrganizationUserGroupDto): void {
    if (group.id === this.selectedGroupId()) return;
    if (!this.hasUnsavedChanges()) {
      this.switchToGroup(group.id);
      return;
    }

    const dialogData: SimpleDialogData = {
      title: this.translocoService.translate('common.buttons.discard'),
      messages: [this.translocoService.translate('dataManagement.userData.texts.discardChangesConfirmation')],
      icon: 'warning',
    };
    this.openDialog(SimpleConfirmDialogComponent, dialogData, () => this.switchToGroup(group.id));
  }

  protected setPermissionsEnabled(isRestricted: boolean): void {
    this.permissionsForm.controls.isRestricted.setValue(isRestricted);
  }

  protected toggleFolderExpanded(directoryId: number): void {
    this.collapsedIds.update(ids => {
      const next = new Set(ids);
      if (!next.delete(directoryId)) next.add(directoryId);
      return next;
    });
  }

  protected setFolderLevel(directoryId: number, level: UserGroupDirectoryPermission): void {
    if (isNoAccess(level) && this.sharedParentIds().has(directoryId)) return;
    const parentById = this.parentById();
    const levels = new Map(this.levels()).set(directoryId, level);
    const autoSharedIds = new Set(this.autoSharedIds());
    autoSharedIds.delete(directoryId);
    const changedIds = new Set([directoryId]);
    if (!isNoAccess(level)) {
      for (let id = parentById.get(directoryId) ?? null; id != null && isNoAccess(levels.get(id)); id = parentById.get(id) ?? null) {
        levels.set(id, Read);
        autoSharedIds.add(id);
        changedIds.add(id);
      }
    } else {
      for (let id = parentById.get(directoryId) ?? null; id != null && autoSharedIds.has(id) && !this.hasSharedDescendant(id, levels); id = parentById.get(id) ?? null) {
        levels.set(id, NoAccess);
        autoSharedIds.delete(id);
        changedIds.add(id);
      }
    }
    this.autoSharedIds.set(autoSharedIds);
    const controls = this.permissionsForm.controls.folders.controls;
    this.folderNodes()
      .filter(({ directory, parentId }) => changedIds.has(directory.id) || (parentId != null && changedIds.has(parentId)))
      .forEach(({ directory, parentId }) => {
        controls[directory.id]?.setValue(storedValueFor(levelAt(levels, directory.id), levelAt(levels, parentId)), { emitEvent: false });
      });
    this.permissionsForm.updateValueAndValidity();
  }

  protected savePermissions(group: OrganizationUserGroupDto): void {
    const { isRestricted, folders } = this.permissionsForm.getRawValue();
    const requests = idsByValue(this.changedFolders()).map(([permission, directoryIds]) =>
      this.userGroupsApiService.setDirectoryPermissions({ userGroupIds: [group.id], directoryIds, permission }, true)
    );
    this.saveSection(group, requests, { isDirectoryPermissionsEnabled: isRestricted }, this.userGroupsApiService.getUncoveredDirectories(true), uncoveredIds => {
      this.directoryPermissions.update(permissions => [
        ...permissions.filter(existing => existing.userGroupId !== group.id),
        ...Object.entries(folders)
          .filter(([, permission]) => permission !== None)
          .map(([directoryId, permission]) => ({ userGroupId: group.id, directoryId: Number(directoryId), permission })),
      ]);
      this.uncoveredDirectoryIds.set(new Set(uncoveredIds));
      this.resetPermissionsForm();
    });
  }

  protected setAllFolderLevels(level: UserGroupDirectoryPermission): void {
    const controls = this.permissionsForm.controls.folders.controls;
    this.folderNodes().forEach(({ directory, parentId }) => {
      controls[directory.id]?.setValue(parentId == null ? storedValueFor(level, NoAccess) : None, { emitEvent: false });
    });
    this.autoSharedIds.set(new Set());
    this.permissionsForm.updateValueAndValidity();
  }

  private hasSharedDescendant(directoryId: number, levels: Map<number, UserGroupDirectoryPermission>): boolean {
    return (this.childIdsById().get(directoryId) ?? []).some(childId => !isNoAccess(levels.get(childId)) || this.hasSharedDescendant(childId, levels));
  }

  protected resetPermissionsForm(): void {
    this.autoSharedIds.set(new Set());
    const { isRestricted, values } = this.savedPermissions();
    syncRecordControls(this.permissionsForm.controls.folders, values, None);
    this.permissionsForm.reset({ isRestricted, folders: values });
  }

  protected setVisibleOrganizationsEnabled(isRestricted: boolean): void {
    this.visibilityForm.controls.isRestricted.setValue(isRestricted);
  }

  protected setOrganizationVisible(organizationId: number, isVisible: boolean): void {
    this.visibilityForm.controls.organizations.controls[organizationId]?.setValue(isVisible);
  }

  protected saveVisibility(group: OrganizationUserGroupDto): void {
    const { isRestricted, organizations } = this.visibilityForm.getRawValue();
    const requests = idsByValue(this.changedOrganizations()).map(([isVisible, organizationIds]) =>
      this.userGroupsApiService.setVisibleOrganizations({ userGroupIds: [group.id], organizationIds, isVisible }, true)
    );
    this.saveSection(group, requests, { isVisibleOrganizationsEnabled: isRestricted }, this.userGroupsApiService.getUncoveredOrganizations(true), uncoveredIds => {
      this.visibleOrganizations.update(visible => [
        ...visible.filter(existing => existing.userGroupId !== group.id),
        ...Object.entries(organizations)
          .filter(([, isVisible]) => isVisible)
          .map(([organizationId]) => ({ userGroupId: group.id, organizationId: Number(organizationId) })),
      ]);
      this.uncoveredOrganizationIds.set(new Set(uncoveredIds));
      this.resetVisibilityForm();
    });
  }

  protected resetVisibilityForm(): void {
    const { isRestricted, values } = this.savedVisibility();
    syncRecordControls(this.visibilityForm.controls.organizations, values, false);
    this.visibilityForm.reset({ isRestricted, organizations: values });
  }

  protected openEditMembersDialog(group: OrganizationUserGroupDto): void {
    const data: AdminGroupMemberDialogData = { group, users: this.users() };
    this.openDialog<AdminGroupMemberDialogResult>(AdminGroupMemberDialogComponent, data, ({ added, removed }) => {
      const requests = [
        ...(added.length ? [this.userGroupsApiService.addMembers(group.id, { userIds: added }, true)] : []),
        ...(removed.length ? [this.userGroupsApiService.removeMembers(group.id, { userIds: removed }, true)] : []),
      ];
      if (requests.length) {
        this.save(
          forkJoin(requests).pipe(
            switchMap(() => forkJoin({ groups: this.userGroupsApiService.getGroups(true), users: this.userGroupsApiService.getUsersWithGroups(true) })),
            tap(({ groups, users }) => {
              this.allGroups.set(groups);
              this.users.set(users);
            })
          )
        );
      }
    });
  }

  protected openCreateGroupDialog(): void {
    const data: AdminGroupDialogData = { mode: 'create', existingNames: this.groupNamesExcept() };
    this.openDialog<AdminGroupDialogResult>(AdminGroupDialogComponent, data, ({ name }) =>
      this.save(this.userGroupsApiService.createGroup({ name }, true).pipe(switchMap(id => this.loadData(id))))
    );
  }

  protected openRenameGroupDialog(group: OrganizationUserGroupDto): void {
    const data: AdminGroupDialogData = { mode: 'rename', name: group.name, existingNames: this.groupNamesExcept(group) };
    this.openDialog<AdminGroupDialogResult>(AdminGroupDialogComponent, data, ({ name }) =>
      this.save(this.userGroupsApiService.updateGroup(group.id, { name }, true).pipe(tap(() => this.updateGroupLocally(group.id, { name }))))
    );
  }

  protected deleteGroup(group: OrganizationUserGroupDto): void {
    const dialogData: SimpleDialogData = {
      title: this.translocoService.translate('organizationAdmin.groups.deleteConfirm.title', { name: group.name }),
      messages: [this.translocoService.translate('organizationAdmin.groups.deleteConfirm.message')],
      icon: 'delete',
    };
    this.openDialog(SimpleConfirmDialogComponent, dialogData, () =>
      this.save(this.userGroupsApiService.deleteGroup(group.id, true).pipe(switchMap(() => this.loadData(EMPTY_GUID))))
    );
  }

  private translatedOptions<T>(options: [T, string][]): Signal<SegmentedToggleOption<T>[]> {
    return computed<SegmentedToggleOption<T>[]>(() => {
      this.activeLang();
      return options.map(([value, key]) => ({ value, label: this.translocoService.translate(key) }));
    });
  }

  private groupNamesExcept(group?: OrganizationUserGroupDto): Set<string> {
    return new Set(
      this.allGroups()
        .filter(existing => existing.id !== group?.id)
        .map(existing => existing.name.toLowerCase())
    );
  }

  private switchToGroup(id: string): void {
    this.selectedGroupId.set(id);
    this.resetPermissionsForm();
    this.resetVisibilityForm();
  }

  private openDialog<R extends AdminDialogResult<unknown> = AdminDialogResult<void>>(
    component: ComponentType<unknown>,
    data: unknown,
    onConfirm: (data: ConfirmedData<R>) => void
  ): void {
    this.dialog
      .open<unknown, unknown, AdminDialogResult<ConfirmedData<R>>>(component, getDialogConfig(data, '480px'))
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result?.event === DialogActions.CONFIRM) onConfirm(result.data);
      });
  }

  private saveSection(
    group: OrganizationUserGroupDto,
    requests: Observable<unknown>[],
    change: GroupSwitch,
    reloadUncovered: Observable<number[]>,
    onSaved: (uncoveredIds: number[]) => void
  ): void {
    const isSwitchChanged = Object.entries(change).some(([key, value]) => group[key as keyof GroupSwitch] !== value);
    const settingsSaved: Observable<unknown> = requests.length ? forkJoin(requests) : of(undefined);
    this.save(
      settingsSaved.pipe(
        switchMap(() => (isSwitchChanged ? this.userGroupsApiService.updateGroup(group.id, change, true) : of(undefined))),
        switchMap(() => reloadUncovered),
        tap(uncoveredIds => {
          this.updateGroupLocally(group.id, change);
          onSaved(uncoveredIds);
        })
      )
    );
  }

  private save(request: Observable<unknown>): void {
    this.isSaving.set(true);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.isSaving.set(false),
      error: (error: HttpErrorResponse) => {
        this.snackbarService.showError(getApiErrorMessage(error));
        this.reload();
      },
    });
  }

  private updateGroupLocally(id: string, changes: Partial<OrganizationUserGroupDto>): void {
    this.allGroups.update(groups => groups.map(group => (group.id === id ? { ...group, ...changes } : group)));
  }

  private reload(): void {
    this.loadData()
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.isSaving.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({ error: error => this.snackbarService.showError(getApiErrorMessage(error)) });
  }

  private loadData(groupIdToSelect = this.selectedGroupId()): Observable<unknown> {
    return forkJoin({
      groups: this.userGroupsApiService.getGroups(true),
      users: this.userGroupsApiService.getUsersWithGroups(true),
      directories: this.userGroupsApiService.getDirectories(true),
      directoryPermissions: this.userGroupsApiService.getDirectoryPermissions(undefined, true),
      uncoveredDirectoryIds: this.userGroupsApiService.getUncoveredDirectories(true),
      linkedOrganizations: this.linkedOrganizationApiService.getLinkedOrganizations(true),
      visibleOrganizations: this.userGroupsApiService.getVisibleOrganizations(true),
      uncoveredOrganizationIds: this.userGroupsApiService.getUncoveredOrganizations(true),
    }).pipe(
      tap(data => {
        this.allGroups.set(data.groups);
        this.users.set(data.users);
        this.directories.set(data.directories);
        this.directoryPermissions.set(data.directoryPermissions);
        this.uncoveredDirectoryIds.set(new Set(data.uncoveredDirectoryIds));
        this.linkedOrganizations.set(data.linkedOrganizations);
        this.visibleOrganizations.set(data.visibleOrganizations);
        this.uncoveredOrganizationIds.set(new Set(data.uncoveredOrganizationIds));
        const selectedId = this.groups().some(group => group.id === groupIdToSelect) ? groupIdToSelect : (this.groups()[0]?.id ?? EMPTY_GUID);
        this.switchToGroup(selectedId);
      })
    );
  }
}
